import { graphql, type ExtractEnum, type VariablesOf } from 'graphql-schema';
import moment from 'moment-timezone';

import { leagueStageFilter, type LeagueStageEnum } from '../../shared/league-status-enum';
import { MembershipPerksEnum } from '../../shared/membership-enums';

const GET_LEAGUE = graphql(`
  query leagues($where: leagues_bool_exp!) {
    leagues(where: $where) {
      _id
    }
  }
`);

export type Filters = {
  /**
   * Filters that appear on the admin ops dashboard should ignore date filters
   * * This placement might not be accurate but it satisfies the uses on dashboard for now
   */
  isHost?: boolean;
  organizationId?: string;
  cityName?: string;
  /** Format "YY/MM/DD" */
  dateLow?: string;
  /** Format "YY/MM/DD" */
  dateHigh?: string;
  daysOfWeek?: ExtractEnum<'day_of_week_enum'>[];
  // Will need to confirm if this is correct, not used in planning sheet
  genders?: string[];
  membershipPerks?: MembershipPerksEnum[];
  neighborhoodIds?: string[];
  // Will need to confirm if this is correct, not used in planning sheet
  perks?: string[];
  programStatus?: LeagueStageEnum | 'CLEAR';
  programTagIds?: string[];
  programTypes?: ExtractEnum<'program_type_enum'>[];
  // Will need to confirm if this is correct, not used in planning sheet
  registrantTypes?: ExtractEnum<'registrant_type_enum'>[];
  seasonNames?: string[];
  // Will need to confirm if this is correct, not used in planning sheet
  selectedPrograms?: string[];
  // Will need to confirm if this is correct, not used in planning sheet
  skillLevels?: string[];
  sportNames?: string[];
  // Will need to confirm if this is correct, not used in planning sheet
  teamNames?: string[];
  timeLow?: string;
  timeHigh?: string;
  venueIds?: string[];
  // Will need to confirm if this is correct, not used in planning sheet
  view?: string;
};

type UpdateSearchUrls = { updateSearchUrls: (update: Partial<WithPropsFromUrl>) => unknown };

export type WithPropsFromUrl = Filters & UpdateSearchUrls;

export type SharedFilterProps = {
  disabled?: boolean;
  className?: string;
  hideIcon?: boolean;
  hideIndicator?: boolean;
  hidePopular?: boolean;
  isHost?: boolean;
  isMulti?: boolean;
  miscStyles?: CSSStyleDeclaration;
  onSelect: any; // TODO
  organizationId: string;
  showAllOptions?: boolean;
  skip?: boolean;
};

export const filtersWhereClause: (
  filters: Filters,
  /**
   * By default, all filters are included.
   * If we want to ignore a given filter, set its value to false and it will be ignored.
   * For example: the sport filter should NOT take into account currently selected sports as that will prevent filtering on multiple sports.
   */
  include?: {
    daysOfWeek?: boolean;
    neighborhoods?: boolean;
    programStatus?: boolean;
    programTypes?: boolean;
    seasons?: boolean;
    sports?: boolean;
    venues?: boolean;
    dateLow?: boolean;
    dateHigh?: boolean;
  }
) => VariablesOf<typeof GET_LEAGUE>['where'] = (
  filters,
  {
    daysOfWeek = true,
    neighborhoods = true,
    programStatus = true,
    programTypes = true,
    seasons = true,
    sports = true,
    venues = true,
    dateLow = true,
    dateHigh = true,
  } = {}
) => ({
  ...(filters?.organizationId ? { organization: { _eq: filters.organizationId } } : {}),
  ...(filters?.cityName ? { organizationByOrganization: { name: { _eq: filters.cityName } } } : {}),
  ...(!filters.isHost && ((dateLow && filters?.dateLow) || (dateHigh && filters?.dateHigh))
    ? {
        start_date: {
          ...(filters?.dateLow
            ? { _gte: moment(filters.dateLow, 'YY/MM/DD').format('YYYY-MM-DD') }
            : {}),
          ...(filters?.dateHigh
            ? { _lte: moment(filters.dateHigh, 'YY/MM/DD').format('YYYY-MM-DD') }
            : {}),
        },
      }
    : {}),
  ...(filters?.daysOfWeek && daysOfWeek
    ? { days_of_weeks: { day_of_week: { _in: filters.daysOfWeek } } }
    : {}),
  ...(filters?.membershipPerks &&
  filters.membershipPerks.includes(MembershipPerksEnum.IS_VOLO_PASS_EXCLUSIVE)
    ? { is_volo_pass_exclusive: { _eq: true } }
    : {}),
  ...(filters?.membershipPerks && filters.membershipPerks.includes(MembershipPerksEnum.IS_PREMIER)
    ? { is_premier: { _eq: true } }
    : {}),
  ...(filters?.neighborhoodIds && neighborhoods
    ? { neighborhood: { _in: filters.neighborhoodIds } }
    : {}),
  ...(filters?.programStatus && filters?.programStatus !== 'CLEAR' && programStatus
    ? leagueStageFilter(filters.programStatus)
    : {}),
  ...(filters?.programTypes && filters.programTypes.length && programTypes
    ? { program_type: { _in: filters.programTypes } }
    : {}),
  ...(filters?.seasonNames && seasons
    ? { seasonBySeason: { name: { _in: filters.seasonNames } } }
    : {}),
  ...(filters?.sportNames && sports ? { sportBySport: { name: { _in: filters.sportNames } } } : {}),
  ...(filters?.venueIds && venues ? { venue: { _in: filters.venueIds } } : {}),
});
